import React, { useCallback, useEffect, useState } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import { useTranslation } from 'react-i18next';
import { RootState } from 'src/configs/store';
import _ from 'lodash';
import KeyboardEventHandler from 'react-keyboard-event-handler';
import jwt from 'jsonwebtoken';
import classNames from 'classnames';
import { differenceInMilliseconds, format, isAfter } from 'date-fns';

import Icon from 'src/components/Icon/Icon';
import CashLog from './components/CashLog/CashLog';
import Button from '../../Button/Button';
import './RetailFooter.scss';
import Calculator from './components/Calculator/Calculator';

function RetailFooter() {
  const { tillUser, customer, tillWallets } = useSelector((state:RootState) => state.auth);
  const { toGet } = useSelector((state:RootState) => state.peripherals.payments);
  const { showReceipt, autoTransactions } = useSelector((state:RootState) => state.peripherals);
  const { divisor } = useSelector((state: RootState) => state.betslip);
  const { functionality } = useSelector((state: RootState) => state.content);
  const [showDrawerButton, setShowDrawerButton] = useState(false);

  const activeWallet = useSelector((state: RootState) => state.auth.localWalletSelected);
  const footerTill = localStorage.getItem('footerTill');

  const [showCashLog, setShowCashLog] = useState(false);
  const [showCalculator, setShowCalculator] = useState(false);
  const [customerInfoHidden, setCustomerInfoHidden] = useState(true);

  const decodedToken:any = jwt.decode(tillUser);

  const dispatch = useDispatch();
  const { t } = useTranslation();
  const toGetValue = +(toGet / divisor).toFixed(2);

  const handleModal = useCallback(() => {
    setShowCashLog(!showCashLog);
    setShowCalculator(false);
  }, [showCashLog]);

  const checkDrawer = async () => {
    const response = await dispatch.peripherals.getDrawerStatus();
    dispatch.peripherals.getTillBalance();

    if (tillWallets.length === 0){
      await dispatch.auth.getTillWallets();
    }
    setShowDrawerButton(response);
  };

  useEffect(() => {
    checkDrawer();
  }, []);

  const loadTillTransactions = async() => {
    await dispatch.peripherals.getTillTransactions();
    if (_.isEmpty(autoTransactions)){
      await dispatch.peripherals.getAutoTransactionsFunctions();
    }
  };

  const onNextClick = async () => {
    dispatch.betslip.clearBets();
    dispatch.lotto.resetLottoFastModeHistory();
    dispatch.lotto.resetRetailValues();

    if (!_.isEmpty(customer)){
      dispatch.auth.tillCustomerLogout();
    }

    await dispatch.peripherals.sendTillCommand({ action: 1, info: 'next_delete', value: 0 });
    loadTillTransactions();
  };

  const openDrawer = async (e) => {
    e.preventDefault();
    await dispatch.peripherals.openDrawer();
  };

  const onPayoutClick = async () => {
    dispatch.peripherals.lastPayout();
  };

  useEffect(() => {
    loadTillTransactions();
  }, []);

  const handleKeys = (key, e) => {
    if (e.key === 'F2'){
      onNextClick();
    } else if (e.key === '\\'){
      openDrawer(e);
    } else {
      const func = _.find(functionality, { key: e.key });
      if (func && func.name === 'calculator'){
        e.preventDefault();
        setShowCalculator(!showCalculator);
        setShowCashLog(false);
      } else if (func && func.name === 'cashLog'){
        setShowCashLog(!showCashLog);
        setShowCalculator(false);
      }
    }
  };

  const expiredToken = localStorage.getItem('exp');

  const checkLogout = () => {
    if (isAfter(new Date(), +expiredToken * 1000)){
      dispatch.auth.tillLogout({ showInactivityMessage: false, hardLogout: false, expired:true });
    }
  };

  const diff = differenceInMilliseconds(+expiredToken * 1000, new Date());
  const timerInterval = setInterval(checkLogout, diff);

  useEffect(() => {
    return () => {
      clearInterval(timerInterval);
    };
  }, [timerInterval, expiredToken]);

  const handleCalculator = () => {
    setShowCalculator(!showCalculator);
    setShowCashLog(false);
  };

  const handleClose = useCallback(() => {
    setShowCalculator(false);
  }, []);

  useEffect(() => {
    dispatch.peripherals.updateSessionApproval(footerTill?.toString().length >= 4);
    dispatch.peripherals.updateTillHistory(footerTill?.toString().length >= 4);
  }, [footerTill]);

  return (
    <>
      {
        showCashLog && <CashLog handleModal={ handleModal } />
      }
      {
        showCalculator && <Calculator onClose={ handleClose } />
      }
      <div className="retail-footer__bottom">
        <div className="retail-footer__functions">
          <button
            className={
              classNames({
                'retail-footer__history': true,
                'retail-footer__history--selected': showCashLog
              })
            }
            onClick={ handleModal }
          >
            <Icon
              className={
                classNames({
                  'retail-footer__history-icon': true,
                  'retail-footer__history-icon--selected': showCashLog
                })
              }
              icon={ showCashLog ? 'cashLog' : 'history' }
            />
          </button>
          <button
            className={
              classNames({
                'retail-footer__history': true,
                'retail-footer__history--selected': showCalculator
              })
            }
            onClick={ handleCalculator }
          >
            <Icon
              className={
                classNames({
                  'retail-footer__history-icon': true,
                  'retail-footer__history-icon--selected': showCalculator
                })
              }
              icon={ showCalculator ? 'calculator' : 'calculator' }
            />
          </button>
        </div>
        <div className="retail-footer__customer-container">
          <div className="retail-footer__to-get">
            <span>{ +toGetValue >= 0 ? t('footer.toGet') : t('footer.toPay') }:</span>
            <span>{ +toGetValue >= 0 ? toGetValue :  +toGetValue*-1 }</span>
          </div>
        </div>
        {
          !_.isEmpty(customer) && (
            <div className="retail-footer__customer-container">
              <div className="retail-footer__customer">
                <span>{ t('footer.customer') }</span>
                <Icon
                  icon={ customerInfoHidden ? 'eyeOpen' : 'eyeClosed' }
                  onClick={ () => (setCustomerInfoHidden(!customerInfoHidden)) }
                />
              </div>

              {
                !customerInfoHidden && (
                  <>
                    <div className="retail-footer__id">
                      <span>{ t('footer.id') }:</span>
                      <span>{ customer.id }</span>
                    </div>

                    <div className="retail-footer__balance">
                      <span>{ t('footer.balance') }:</span>
                      <span>{ activeWallet && ((activeWallet?.balance / activeWallet?.divisor) || 0) }</span>
                    </div>
                  </>
                )
              }
            </div>
          )
        }

        <KeyboardEventHandler
          handleFocusableElements={ true }
          handleKeys={ ['all'] }
          onKeyEvent={ handleKeys }
        />
        {
          showReceipt && <Button
            className="btn--primary retail-footer__button"
            size="medium"
            onClick={ onPayoutClick }
            name="logout"
            text={ t('footer.payoutReceipt') }
          />
        }
        <div>
          <Button
            className="btn--primary retail-footer__button"
            size="medium"
            onClick={ onNextClick }
            name="logout"
            text={ t('footer.next') }
          />
          {
            showDrawerButton && <Button
              className="btn--primary retail-footer__button"
              size="medium"
              onClick={ openDrawer }
              name="open"
              text={ t('footer.open') }
            />
          }
        </div>
      </div>
      <div className="retail-footer__bottom">
        <div className="retail-footer__info">
          { /*  TEMPORARY REMOVE <span>{ t('footer.license') }:</span>
          <span>xxxx</span> */ }
          <span>{ t('footer.session') }</span>
          <span>{ localStorage.getItem('tillSession') }</span>
          <span>{ t('footer.tillCode') }:</span>
          <span>{ localStorage.getItem('footerTill') || '--' }</span>
          <span>{ t('footer.shop') }:</span>
          <span>{ localStorage.getItem('shop') }</span>
          <span>{ t('footer.company') }:</span>
          <span>{ localStorage.getItem('company') }</span>
          <span>{ t('footer.user') }:</span>
          <span>{
            decodedToken?.username ? `${ localStorage.getItem('tilluserId') ?
              localStorage.getItem('tilluserId') + ' - ':
              '' } ${ decodedToken?.username }` : '--'
          }</span>
          <span>{ t('footer.sessionStarted') }:</span>
          <span>{ format(+(localStorage.getItem('sessionStart')) * 1000, 'dd/MM/yyyy - HH:mm:ss') }</span>
          <span>{ t('footer.loggedInSince') }:</span>
          <span>{ format(+(localStorage.getItem('firstLogin')) * 1000, 'dd/MM/yyyy - HH:mm:ss') }</span>
        </div>
      </div>
    </>
  );
}

export default RetailFooter;
